import { useEffect, useRef } from 'react';
import { T } from 'gt-react';
import { format } from 'date-fns';
import { RobotIcon, SpinnerIcon } from '@phosphor-icons/react';
import {
  usePaymentAiReview,
  useRunPaymentAiReview,
} from '@/api/rpc/hooks/finance/usePayments';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { Payment } from '@erp/rpc';

export interface PaymentAiReviewEntryProps {
  payment: Payment;
}

function isUnpaidStatus(status: string): boolean {
  return (
    status === 'in_review' ||
    status === 'approved' ||
    status === 'scheduled' ||
    status === 'pending' ||
    status === 'processing'
  );
}

export function PaymentAiReviewEntry({ payment }: PaymentAiReviewEntryProps) {
  const { review, isLoading } = usePaymentAiReview(payment.id);
  const runReview = useRunPaymentAiReview();

  const autoRunAttempted = useRef<string | null>(null);

  useEffect(() => {
    if (!review || isLoading) return;
    if (autoRunAttempted.current === payment.id) return;
    if (!isUnpaidStatus(payment.status)) return;
    if (review.status !== 'idle') return;
    if (runReview.isPending) return;

    autoRunAttempted.current = payment.id;
    runReview.mutate({
      paymentId: payment.id,
      triggerReason: 'auto-run on payment detail open',
    });
  }, [payment.id, payment.status, review, isLoading, runReview]);

  if (!review) {
    return null;
  }

  const handleRerun = () => {
    runReview.mutate({
      paymentId: payment.id,
      triggerReason: 'manual re-run from payment detail',
    });
  };

  const isRunning = review.isRunning || runReview.isPending;
  const reviewedAtFormatted = review.reviewedAt
    ? format(new Date(review.reviewedAt), 'PPp')
    : null;

  return (
    <div className="flex flex-col gap-1 rounded-md border border-dashed border-border bg-muted/30 p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <RobotIcon className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">
            <T>Payment Reviewer Agent</T>
          </span>
          <StatusBadge status={review.status} isRunning={isRunning} />
        </div>
        <div className="flex items-center gap-2">
          {reviewedAtFormatted && !isRunning && (
            <span className="text-xs text-muted-foreground">
              {reviewedAtFormatted}
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRerun}
            disabled={isRunning}
          >
            {isRunning ? (
              <>
                <SpinnerIcon className="mr-1 h-3 w-3 animate-spin" />
                <T>Running</T>
              </>
            ) : review.status === 'idle' ? (
              <T>Run</T>
            ) : (
              <T>Re-run</T>
            )}
          </Button>
        </div>
      </div>

      {isRunning && !review.reasoningSummary ? (
        <p className="text-xs text-muted-foreground italic">
          <T>Reviewing payment — this usually takes less than a minute.</T>
        </p>
      ) : review.status === 'idle' ? (
        <p className="text-xs text-muted-foreground italic">
          <T>Not reviewed yet.</T>
        </p>
      ) : (
        <>
          {review.reasoningSummary ? (
            <p className="text-xs text-muted-foreground italic">
              &ldquo;{review.reasoningSummary}&rdquo;
            </p>
          ) : review.error ? (
            <p className="text-xs text-destructive">{review.error}</p>
          ) : null}

          {review.blockingReasons.length > 0 && (
            <ul className="mt-1 list-disc pl-5 text-xs text-destructive">
              {review.blockingReasons.map((reason, idx) => (
                <li key={idx}>{reason}</li>
              ))}
            </ul>
          )}

          {review.issues.length > 0 && review.blockingReasons.length === 0 && (
            <ul className="mt-1 list-disc pl-5 text-xs text-muted-foreground">
              {review.issues.map((issue, idx) => (
                <li key={idx}>{issue}</li>
              ))}
            </ul>
          )}

          {review.checksPerformed.length > 0 && (
            <div className="mt-2">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                <T>Checks performed</T>
              </p>
              <ul className="mt-1 space-y-0.5 text-xs">
                {review.checksPerformed.map((check, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckStatusMark status={check.status} />
                    <span className="font-medium">{`${check.category}:`}</span>
                    <span className="text-muted-foreground">
                      {check.detail}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CheckStatusMark({
  status,
}: {
  status: 'pass' | 'fail' | 'warn' | 'na';
}) {
  const map = {
    pass: { symbol: '✓', className: 'text-green-700' },
    fail: { symbol: '✕', className: 'text-destructive' },
    warn: { symbol: '!', className: 'text-amber-600' },
    na: { symbol: '–', className: 'text-muted-foreground' },
  } as const;
  const { symbol, className } = map[status];
  return (
    <span
      aria-label={status}
      className={`inline-block w-3 text-center font-mono ${className}`}
    >
      {symbol}
    </span>
  );
}

function StatusBadge({
  status,
  isRunning,
}: {
  status: string;
  isRunning: boolean;
}) {
  if (isRunning || status === 'running') {
    return (
      <Badge variant="secondary">
        <T>Running</T>
      </Badge>
    );
  }
  if (status === 'passed') {
    return (
      <Badge
        variant="outline"
        className="bg-green-50 text-green-800 border-green-200"
      >
        <T>Passed ✓</T>
      </Badge>
    );
  }
  if (status === 'failed') {
    return (
      <Badge variant="destructive">
        <T>Blocked</T>
      </Badge>
    );
  }
  if (status === 'unavailable') {
    return (
      <Badge variant="outline">
        <T>Unavailable</T>
      </Badge>
    );
  }
  return (
    <Badge variant="outline">
      <T>Not run</T>
    </Badge>
  );
}
